import { formatStreetAddress } from '../utils/propertyAddress';
import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useEffect, useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import usePropertyDetail from '../hooks/usePropertyDetail';
import PropertyDetailModal from './PropertyDetailModal';
import { neighborhoodBySlug } from '../content/neighborhoods';
import { articleBySlug } from '../content/seoArticles';
import './NeighborhoodPage.css';

const ORIGIN = `https://${SITE_CONFIG.BASE_URL.replace(/^https?:\/\//, '').replace(/^www\./, '')}`;

export default function NeighborhoodPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const area = neighborhoodBySlug[slug];
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { propertyDetail, setPropertyDetail, propertyDetailImages, imagesLoading, openPropertyFromSummary } = usePropertyDetail();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; };
  }, [isDarkMode]);

  useEffect(() => {
    if (!area) return;
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({
      firstPhoto: 'true', hasMedia: 'true', listingDateSort: 'newold',
      state: area.state, city: area.searchCity
    });
    propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?${params}`, {})
      .then((response) => response.ok ? response.json() : null)
      .then((data) => { if (active) setProperties(data?.properties || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [area]);

  if (!area) {
    return <main className="area-page"><div className="area-shell"><Link to="/">← Home</Link><h1>Area not found</h1></div></main>;
  }

  const title = `${area.name}, CA Homes for Sale & Real Estate Guide | ${SITE_CONFIG.AGENT.NAME}`;
  const description = `Explore ${area.name} homes for sale, neighborhoods, housing styles, and local buyer guidance with ${SITE_CONFIG.AGENT.NAME} of ${SITE_CONFIG.AGENT.BROKERAGE}.`;
  const canonical = `${ORIGIN}/area/${area.slug}`;
  const resources = (area.relatedArticles || []).map((resourceSlug) => articleBySlug[resourceSlug]).filter(Boolean);
  const openSearch = () => navigate('/search', { state: { openGlobeMap: true, initialCity: area.searchCity } });

  return (
    <main className={`area-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'Place', name: `${area.name}, California`,
          description, url: canonical, containedInPlace: { '@type': 'AdministrativeArea', name: area.county }
        })}</script>
      </Helmet>

      <div className="area-shell">
        <Link className="area-home" to="/">← Home</Link>
        <header className="area-hero">
          <span>{area.eyebrow}</span>
          <h1>{area.heroHeadline}</h1>
          <p>Local guidance from Jeff Upp · Serving Southern California buyers and sellers since 1990</p>
        </header>

        <section className="area-prose">
          <h2>Living in {area.name}, {area.county}</h2>
          {area.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </section>

        <section className="area-lifestyle">
          <div>
            <span className="section-label">Local perspective</span>
            <h2>The {area.name} Lifestyle</h2>
            <p>{area.vibe}</p>
          </div>
          <div className="area-amenities">
            {area.amenities.map((amenity) => <article key={amenity.title}><h3>{amenity.title}</h3><p>{amenity.text}</p></article>)}
          </div>
        </section>

        <section className="area-market">
          <span className="section-label">Planning snapshot</span>
          <h2>{area.name} Real Estate</h2>
          <div className="market-grid">
            <div><strong>Typical asking range</strong><span>{area.market.priceRange}</span></div>
            <div><strong>Current value</strong><span>{area.market.medianPrice}</span></div>
            <div><strong>Market pace</strong><span>{area.market.dom}</span></div>
            <div><strong>Common home types</strong><span>{area.market.homeTypes}</span></div>
            <div><strong>Often a fit for</strong><span>{area.buyerProfile}</span></div>
          </div>
          <p className="market-note">Planning ranges change with inventory and are not appraisals. Ask Jeff for a current, property-specific analysis.</p>
        </section>

        <section className="area-listings">
          <div className="section-heading-row"><div><span className="section-label">Live CRMLS search</span><h2>{area.name} Homes for Sale</h2></div><button onClick={openSearch}>Search all {area.name} homes →</button></div>
          {loading ? <p>Loading current listings…</p> : properties.length ? (
            <div className="area-property-grid">
              {properties.slice(0, 9).map((property) => (
                <button key={property.id} className="area-property" onClick={() => openPropertyFromSummary(property)}>
                  {property.first_photo_url && <img src={property.first_photo_url} alt={formatStreetAddress(property)} />}
                  <span><strong>{property.current_price ? `$${Number(property.current_price).toLocaleString()}` : 'Price available on request'}</strong>{formatPropertyAddress(property)}</span>
                </button>
              ))}
            </div>
          ) : <p>No active listings are available in this feed right now. <button className="inline-search" onClick={openSearch}>Open the full property search.</button></p>}
        </section>

        <section className="area-resources">
          <span className="section-label">Helpful resources</span>
          <h2>Plan Your {area.name} Move</h2>
          <div className="resource-grid">
            {resources.map((article) => <Link key={article.slug} to={`/guide/${article.slug}`}>{article.h1}<span>Read guide →</span></Link>)}
          </div>
        </section>

        <section className="area-cta">
          <img src="/headshot.jpg" alt="Jeff Upp" />
          <div><span className="section-label">Local experience since 1990</span><h2>Talk With Jeff About {area.name}</h2><p>Get straightforward guidance for your purchase, sale, relocation, or investment.</p><div><a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>Call (909) 519-1114</a><a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>Email Jeff</a></div></div>
        </section>
      </div>

      <div className="area-mls"><img src={SITE_CONFIG.MLS.LOGO_URL} alt="CRMLS" /><span>{SITE_CONFIG.MLS.DISCLAIMER} · Brokerage DRE #{SITE_CONFIG.AGENT.BROKERAGE_LICENSE} · Agent DRE #{SITE_CONFIG.AGENT.LICENSE}</span></div>

      {propertyDetail && <PropertyDetailModal propertyDetail={propertyDetail} propertyDetailImages={propertyDetailImages} imagesLoading={imagesLoading} onClose={() => setPropertyDetail(null)} user={null} isAdmin={false} likedProperties={likedProperties} setLikedProperties={setLikedProperties} />}
    </main>
  );
}
